import {SaveAlt} from '@mui/icons-material';
import {Typography} from '@mui/material';
import Box from '@mui/material/Box';
import {
  DataGrid,
  GridColDef,
  GridRowId,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
} from '@mui/x-data-grid';
import React, {useEffect, useState} from 'react';
import FileDownloadButton from 'Shared/components/FileDownloadButton';
import {FileExtension} from 'Shared/components/FileDownloadLink';
import {RequestMethod} from 'Shared/utilities/fetchHelpers';
import './CommissionsGrid.scss';
import {A360CommissionRequestPayload} from '../types';
import {
  DEFAULT_SELECTED_PAGE,
  DEFAULT_GRID_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
} from 'Agent/Commissions/ducks/a360Commissions';
import {A360CommissionsSectionError} from '../Placeholders';
import {themeColors} from 'Theme';

export type FetchParams = {
  page: number;
  pageSize: number;
  sortModel: GridSortModel;
};

export type A360CommissionsGridProps<T> = {
  columns: GridColDef[];
  error?: any;
  getRowId?: (row: T) => GridRowId;
  loading: boolean;
  rows: T[];
  totalRecords: number;
  onFetch: (params: FetchParams) => void;
  initialSort?: GridSortModel;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  title?: string;
  toolbarProps?: {
    exportToCsv: {
      requestPayload: A360CommissionRequestPayload;
      downloadedFileName: string;
      relativeFileLocation: string;
    };
  };
};

const NoRecordsFound = () => (
  <Typography
    style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%'}}
  >
    No Records Found
  </Typography>
);

export const A360CommissionsGrid = <T,>(props: A360CommissionsGridProps<T>) => {
  const {
    initialPageSize,
    initialSort,
    onFetch,
    columns,
    toolbarProps,
    title,
    getRowId,
    loading,
    rows,
    totalRecords,
    pageSizeOptions,
    error,
  } = props;

  const [page, setPage] = useState<number>(DEFAULT_SELECTED_PAGE ?? 15);
  const [pageSize, setPageSize] = useState<number>(initialPageSize ?? DEFAULT_GRID_PAGE_SIZE);
  const [sortModel, setSortModel] = useState<GridSortModel>(initialSort ?? []);

  useEffect(() => {
    onFetch?.({page, pageSize, sortModel});
  }, [onFetch]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    onFetch?.({page: newPage, pageSize, sortModel});
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(DEFAULT_SELECTED_PAGE);
    onFetch?.({page, pageSize: newPageSize, sortModel});
  };

  const handleSortChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    setPage(DEFAULT_SELECTED_PAGE);
    onFetch?.({page, pageSize, sortModel: newSortModel});
  };

  return (
    <Box sx={{width: '100%'}}>
      {error ? (
        <A360CommissionsSectionError error={error} />
      ) : (
        <DataGrid
          autoHeight
          columns={columns ?? []}
          components={{
            NoRowsOverlay: NoRecordsFound,
            Toolbar: () => <A360CommissionsGridHeader toolbar={toolbarProps} title={title} />,
          }}
          disableColumnMenu
          disableSelectionOnClick
          getRowId={getRowId}
          loading={loading ?? false}
          localeText={{
            toolbarColumns: 'Edit Columns',
          }}
          page={page}
          sortModel={sortModel}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSortModelChange={handleSortChange}
          pageSize={pageSize}
          pagination
          paginationMode="server"
          rows={rows ?? []}
          rowCount={totalRecords ?? 0}
          rowsPerPageOptions={pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS}
          sortingMode="server"
          getRowClassName={params =>
            params.indexRelativeToCurrentPage % 2 === 0
              ? 'a360-commissions-grid__row a360-commissions-grid__row--even'
              : 'a360-commissions-grid__row a360-commissions-grid__row--odd'
          }
          sx={{border: 'none'}}
        />
      )}
    </Box>
  );
};

const A360CommissionsGridHeader = (headerProps: {
  title?: string;
  toolbar: A360CommissionsGridProps<any>['toolbarProps'];
}) => {
  const csvProps = {...exportToCsvConfig, ...headerProps?.toolbar?.exportToCsv};

  return (
    <div className="a360-commissions-grid__main-header">
      {headerProps.title && (
        <Typography component="h2" className="a360-commissions-grid__title" sx={{fontSize: '1.5rem', fontWeight: 700}}>
          {headerProps.title}
        </Typography>
      )}
      <GridToolbarContainer className="a360-commissions-grid__toolbar">
        <FileDownloadButton
          {...csvProps}
          aria-label="Export to CSV"
          variant="contained"
          color="primary"
          gridOverrideClassName="a360-commissions-grid__toolbar-csv"
        />
        {/* @ts-ignore */}
        <GridToolbarColumnsButton
          size="medium"
          variant="outlined"
          sx={{
            border: `1px solid ${themeColors.blue_400}`,
            color: `${themeColors.blue_400}`,
            minWidth: '44px',
            minHeight: '44px',
          }}
          aria-label="Edit Columns"
        />
      </GridToolbarContainer>
    </div>
  );
};

const exportToCsvConfig = {
  startIcon: <SaveAlt />,
  extension: FileExtension.CSV,
  children: 'Export To CSV',
  requestMethod: RequestMethod.POST,
  displayTooltip: false,
};
